import {Module} from "@intuitionrobotics/ts-common";
import {XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {HttpMethod} from "@intuitionrobotics/thunderstorm";
import {
    AccountApi_RefreshJWT,
    ApiAssignPermissionGroupToUsers,
    ApiQueryPermissionGroups,
    ApiQueryPermissionUsers,
    ApiResetPermissionForUser,
    ApiUpsertPermissionGroup,
    GroupPermission,
    PushKey_PermissionUpdate
} from "@app-sp/app-shared/shared";
import {AccountApi_ListAccounts, UI_Account} from "@intuitionrobotics/user-account";
import {AccountModule, LoggedStatus, OnLoginStatusUpdated, RequestKey_ValidateSession, StorageKey_UserEmail} from "@intuitionrobotics/user-account/frontend";
import {PushPubSubModule} from "@intuitionrobotics/push-pub-sub/app-frontend/modules/PushPubSubModule";
import {EnvironmentModule} from "@modules/EnvironmentModule";

export class PermissionsModule_Class
    extends Module implements OnLoginStatusUpdated {

    onLoginStatusUpdated = async () => {
        if (AccountModule.getLoggedStatus() !== LoggedStatus.LOGGED_IN)
            return;

        // Offline: no push backend — skip the FCM permission-update subscription.
        if (EnvironmentModule.isOffline())
            return;

        const email = StorageKey_UserEmail.get();

        PushPubSubModule.subscribe({
            pushKey: PushKey_PermissionUpdate,
            props: {userEmail: email},
            callBack: (data) => {
                XhrHttpModule
                    .createRequest<AccountApi_RefreshJWT>(HttpMethod.POST, "refresh-jwt")
                    .setRelativeUrl("/v1/permissions/refresh-jwt")
                    .setOnError((err) => console.error("error regenerating jwt", err))
                    .execute();
            }
        });
    }

    fetchExistingGroups = async () => {
        return XhrHttpModule.createRequest<ApiQueryPermissionGroups>(HttpMethod.GET, "list-groups")
            .setRelativeUrl("/v1/permissions/list-groups")
            .setLabel(`Listing existing groups`)
            .executeSync();

    }

    upsertGroupPermissions(groupPermission: GroupPermission) {
        return XhrHttpModule.createRequest<ApiUpsertPermissionGroup>(HttpMethod.POST, "upsert-group-permissions")
            .setRelativeUrl("/v1/permissions/upsert-group")
            .setJsonBody({group: groupPermission})
            .setLabel(`Upserting group permissions`)
            .executeSync()
    }

    fetchUsers = async () => {
        return XhrHttpModule
            .createRequest<AccountApi_ListAccounts>(HttpMethod.GET, RequestKey_ValidateSession)
            .setLabel(`Fetching users...`)
            .setRelativeUrl("/v1/account/query")
            .executeSync();
    }

    assignGroupToUsers = async (group: GroupPermission, users: UI_Account[], reset = false) => {
        if (group.permissions.units?.read.unitIds?.includes("All")) {
            group.permissions.units.read.unitIds = [".*"];
        }

        if (group.permissions.units?.write.unitIds?.includes("All")) {
            group.permissions.units.write.unitIds = [".*"];
        }

        return XhrHttpModule
            .createRequest<ApiAssignPermissionGroupToUsers>(HttpMethod.POST, "assign-group-to-users")
            .setRelativeUrl("/v1/permissions/assign")
            .setJsonBody({group, users, reset})
            .setLabel(`Assigning group to users`)
            .setOnSuccessMessage(`Group ${group.name} assigned to users ${users.map(u => u.email).join(", ")}`)
            .executeSync()
    }

    fetchPermissionUsers = () => {
        return XhrHttpModule
            .createRequest<ApiQueryPermissionUsers>(HttpMethod.GET, "list-users")
            .setRelativeUrl("/v1/permissions/list-users")
            .setLabel(`Fetching users...`)
            .executeSync()
    }

    resetPermissionForUser(userEmail: string, accountId: string) {
        return XhrHttpModule
            .createRequest<ApiResetPermissionForUser>(HttpMethod.POST, "reset-permission-for-user")
            .setRelativeUrl("/v1/permissions/reset")
            .setLabel(`Resetting permissions for user ${userEmail}`)
            .setJsonBody({userEmail, accountId})
            .executeSync()
    }
}

export const PermissionsModule = new PermissionsModule_Class("PermissionModule");
